import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Seo from '@/components/Seo';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search } from 'lucide-react';
import { ToolCard } from '@/components/tools/ToolCard';
import { CATEGORIES, TOOLS } from '@/lib/tools';
import { TOOL_HUB } from '@/constants/testIds';

export default function ToolsHubPage() {
    const [params, setParams] = useSearchParams();
    const [q, setQ] = useState(params.get('q') || '');
    const [cat, setCat] = useState(params.get('category') || 'all');

    useEffect(() => {
        setQ(params.get('q') || '');
        setCat(params.get('category') || 'all');
    }, [params]);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return TOOLS.filter((t) => {
            if (cat !== 'all' && t.category !== cat) return false;
            if (!needle) return true;
            return (
                t.title.toLowerCase().includes(needle) ||
                t.short.toLowerCase().includes(needle) ||
                t.badges.join(' ').toLowerCase().includes(needle)
            );
        });
    }, [q, cat]);

    const setChip = (id) => {
        const p = new URLSearchParams(params);
        if (id === 'all') p.delete('category');
        else p.set('category', id);
        if (q) p.set('q', q);
        else p.delete('q');
        setParams(p, { replace: true });
    };

    return (
        <>
            <Seo
                title="All tools"
                description="Explore UtilityTool’s growing library of small, focused online tools — file, image, text, utility, and SEO helpers. All free, all fast."
                canonical="/tools"
            />
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-6">
                <p className="text-sm text-slate-500">Tool library</p>
                <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
                    Every UtilityTool tool, in one place
                </h1>
                <p className="mt-2 text-slate-600 max-w-2xl">
                    Search for a task, or pick a category. New tools are added regularly.
                </p>

                <div className="mt-6 flex flex-col md:flex-row md:items-center gap-3">
                    <div className="relative md:max-w-sm flex-1">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            data-testid={TOOL_HUB.search}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search tools…"
                            className="pl-9 h-10 bg-white"
                            aria-label="Filter tools by name"
                        />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <ChipButton active={cat === 'all'} onClick={() => setChip('all')} testid={TOOL_HUB.chipAll}>
                            All
                        </ChipButton>
                        {CATEGORIES.map((c) => (
                            <ChipButton
                                key={c.id}
                                active={cat === c.id}
                                onClick={() => setChip(c.id)}
                                testid={`tool-hub-chip-${c.id}`}
                            >
                                {c.label}
                            </ChipButton>
                        ))}
                    </div>
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                {filtered.length === 0 ? (
                    <div
                        data-testid={TOOL_HUB.emptyState}
                        className="rounded-xl border border-dashed border-border p-10 text-center"
                    >
                        <p className="text-slate-700 font-medium">No tools match “{q}”.</p>
                        <p className="mt-1 text-sm text-slate-500">
                            Try a different keyword or clear the filter.
                        </p>
                        <Button
                            variant="secondary"
                            className="mt-4"
                            onClick={() => {
                                setQ('');
                                setChip('all');
                            }}
                        >
                            Reset filters
                        </Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid={TOOL_HUB.grid}>
                        {filtered.map((t) => (
                            <ToolCard key={t.slug} tool={t} />
                        ))}
                    </div>
                )}
            </section>
        </>
    );
}

function ChipButton({ active, onClick, children, testid }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            className={`inline-flex items-center px-3 h-9 rounded-full border text-sm font-medium transition-colors ${
                active
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-700 border-border hover:bg-slate-50'
            }`}
        >
            {children}
        </button>
    );
}
